import clsx from 'clsx';
import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { Input } from '@/components/ui/input';
import IconArrowUp from '@/assets/icons/icon-arrow-up-03.svg?react';
import IconArrowDown from '@/assets/icons/icon-arrow-down-03.svg?react';

interface TimePickerProps {
  text?: string;
  value?: string; // Format: "HH:MM AM/PM" or "HH:MM" (24-hour)
  onChange?: (value: string) => void;
  className?: string;
  format?: '12' | '24'; // 12-hour or 24-hour format
}

type Period = 'AM' | 'PM';

interface TimeValue {
  hours: number;
  minutes: number;
  period?: Period;
}

const getTimezoneAbbreviation = (): string => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const date = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    });
    const parts = formatter.formatToParts(date);
    const tzPart = parts.find((p) => p.type === 'timeZoneName');
    return tzPart?.value || 'UTC';
  } catch {
    return 'UTC';
  }
};

const parseTimeValue = (value: string, format: '12' | '24'): TimeValue => {
  if (!value) {
    return format === '12' ? { hours: 12, minutes: 0, period: 'AM' } : { hours: 0, minutes: 0 };
  }

  const parts = value.split(' ');
  const timePart = parts[0];
  const periodPart = parts[1] as Period | undefined;

  const [hoursStr, minutesStr] = timePart.split(':');
  let hours = parseInt(hoursStr, 10) || 0;
  const minutes = parseInt(minutesStr, 10) || 0;

  if (format === '12') {
    const period = periodPart || 'AM';
    // Ensure hours are in 1-12 range
    if (hours === 0) hours = 12;
    if (hours > 12) hours = 12;
    return { hours, minutes, period };
  } else {
    // 24-hour format
    if (hours < 0) hours = 0;
    if (hours > 23) hours = 23;
    return { hours, minutes };
  }
};

const formatTimeValue = (time: TimeValue, format: '12' | '24'): string => {
  const { hours, minutes, period } = time;
  const hoursStr = hours.toString().padStart(2, '0');
  const minutesStr = minutes.toString().padStart(2, '0');

  if (format === '12') {
    return `${hoursStr}:${minutesStr} ${period}`;
  } else {
    return `${hoursStr}:${minutesStr}`;
  }
};

const validateAndClamp = (time: TimeValue, format: '12' | '24'): TimeValue => {
  let { hours, minutes } = time;
  const { period } = time;
  // Clamp minutes to 0-59
  if (minutes < 0) minutes = 0;
  if (minutes > 59) minutes = 59;

  if (format === '12') {
    // Clamp hours to 1-12 for 12-hour format
    if (hours < 1) hours = 1;
    if (hours > 12) hours = 12;
    return { hours, minutes, period: period || 'AM' };
  } else {
    // Clamp hours to 0-23 for 24-hour format
    if (hours < 0) hours = 0;
    if (hours > 23) hours = 23;
    return { hours, minutes };
  }
};

export const TimePicker = ({
  text,
  value = '',
  onChange,
  className,
  format = '12',
}: TimePickerProps) => {
  const [timeValue, setTimeValue] = useState<TimeValue>(() => parseTimeValue(value, format));
  const [inputValue, setInputValue] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);
  const [highlightStyle, setHighlightStyle] = useState({ width: 0, left: 0 });
  const inputRef = useRef<HTMLInputElement>(null);
  const periodButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({ AM: null, PM: null });

  const timezone = getTimezoneAbbreviation();

  // Update highlight position for AM/PM toggle
  useLayoutEffect(() => {
    if (format === '12' && timeValue.period) {
      const activeEl = periodButtonRefs.current[timeValue.period];
      if (activeEl) {
        setHighlightStyle({
          width: activeEl.offsetWidth,
          left: activeEl.offsetLeft,
        });
      }
    }
  }, [timeValue.period, format]);

  // Sync with external value changes
  useEffect(() => {
    if (!isFocused && value !== formatTimeValue(timeValue, format)) {
      const parsed = parseTimeValue(value, format);
      setTimeValue(parsed);
    }
  }, [value, format, isFocused]);

  // Update input display when timeValue changes and not focused
  useEffect(() => {
    if (!isFocused) {
      const formatted = `${timeValue.hours.toString().padStart(2, '0')}:${timeValue.minutes.toString().padStart(2, '0')}`;
      setInputValue(formatted);
    }
  }, [timeValue, isFocused]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;

    // Allow only digits and colon
    val = val.replace(/[^\d:]/g, '');

    // Auto-format: add colon after 2 digits
    if (val.length === 2 && !val.includes(':') && inputValue.length < val.length) {
      val = val + ':';
    }

    // Limit format to HH:MM
    if (val.length > 5) {
      val = val.slice(0, 5);
    }

    setInputValue(val);

    // Parse and validate
    if (val.includes(':')) {
      const [hoursStr, minutesStr] = val.split(':');
      const hours = parseInt(hoursStr, 10);
      const minutes = parseInt(minutesStr, 10);

      if (!isNaN(hours) && !isNaN(minutes)) {
        const newTime = validateAndClamp({ hours, minutes, period: timeValue.period }, format);
        setTimeValue(newTime);
        onChange?.(formatTimeValue(newTime, format));
      }
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    const formatted = `${timeValue.hours.toString().padStart(2, '0')}:${timeValue.minutes.toString().padStart(2, '0')}`;
    setInputValue(formatted);
  };

  const handleBlur = () => {
    setIsFocused(false);
    // Validate and format on blur
    const validated = validateAndClamp(timeValue, format);
    setTimeValue(validated);
    onChange?.(formatTimeValue(validated, format));
  };

  const handleIncrement = () => {
    let newMinutes = timeValue.minutes + 1;
    let newHours = timeValue.hours;
    let newPeriod = timeValue.period;

    if (newMinutes > 59) {
      newMinutes = 0;
      newHours += 1;

      if (format === '12') {
        if (newHours > 12) {
          newHours = 1;
        }
        if (newHours === 12) {
          newPeriod = newPeriod === 'AM' ? 'PM' : 'AM';
        }
      } else {
        if (newHours > 23) {
          newHours = 0;
        }
      }
    }

    const newTime = { hours: newHours, minutes: newMinutes, period: newPeriod };
    setTimeValue(newTime);
    onChange?.(formatTimeValue(newTime, format));
  };

  const handleDecrement = () => {
    let newMinutes = timeValue.minutes - 1;
    let newHours = timeValue.hours;
    let newPeriod = timeValue.period;

    if (newMinutes < 0) {
      newMinutes = 59;
      newHours -= 1;

      if (format === '12') {
        if (newHours < 1) {
          newHours = 12;
        }
        if (newHours === 11 && timeValue.hours === 12) {
          newPeriod = newPeriod === 'AM' ? 'PM' : 'AM';
        }
      } else {
        if (newHours < 0) {
          newHours = 23;
        }
      }
    }

    const newTime = { hours: newHours, minutes: newMinutes, period: newPeriod };
    setTimeValue(newTime);
    onChange?.(formatTimeValue(newTime, format));
  };

  const handlePeriodChange = (newPeriod: Period) => {
    const newTime = { ...timeValue, period: newPeriod };
    setTimeValue(newTime);
    onChange?.(formatTimeValue(newTime, format));
  };

  return (
    <div className={clsx('flex w-full items-center justify-between', className)}>
      {/* Label */}
      {text && (
        <label className="text-button-sm font-medium text-neutral-500">
          {text} ({timezone})
        </label>
      )}

      {/* Time Input and AM/PM Toggle */}
      <div className="flex items-center gap-1.5">
        {/* Time Input with Arrows */}
        <div className="relative inline-flex items-center">
          <Input
            ref={inputRef}
            className="h-8 min-h-8 w-[62px] pr-4 pl-2 text-center text-sm"
            type="text"
            value={
              isFocused
                ? inputValue
                : `${timeValue.hours.toString().padStart(2, '0')}:${timeValue.minutes.toString().padStart(2, '0')}`
            }
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder="00:00"
          />

          <div className="pointer-events-none absolute top-1/2 right-0.5 flex -translate-y-1/2 flex-col">
            <button
              type="button"
              tabIndex={-1}
              className="pointer-events-auto cursor-pointer text-neutral-500 transition-all hover:text-neutral-700"
              style={{ lineHeight: 0 }}
              onMouseDown={(e) => {
                e.preventDefault();
                handleIncrement();
              }}
            >
              <IconArrowUp className="h-3 w-3" />
            </button>

            <button
              type="button"
              tabIndex={-1}
              className="pointer-events-auto cursor-pointer text-neutral-500 transition-all hover:text-neutral-700"
              style={{ lineHeight: 0 }}
              onMouseDown={(e) => {
                e.preventDefault();
                handleDecrement();
              }}
            >
              <IconArrowDown className="h-3 w-3" />
            </button>
          </div>
        </div>

        {/* AM/PM Toggle (only show for 12-hour format) - SegmentedControl style */}
        {format === '12' && (
          <div className="relative flex h-8 items-center rounded-md bg-neutral-50 shadow-[inset_0px_0px_2px_0px_rgba(0,0,0,0.12)]">
            {/* Sliding highlight background */}
            <div
              className="absolute top-0 z-20 flex h-full items-center justify-center rounded-md bg-[#ffffff] outline outline-offset-[-1px] outline-[#e9ecf0] transition-all duration-300"
              style={{
                width: `${highlightStyle.width}px`,
                transform: `translateX(${highlightStyle.left}px)`,
              }}
            >
              <p className="text-button-sm !font-semibold text-indigo-500">{timeValue.period}</p>
            </div>

            {/* AM Button */}
            <button
              ref={(el) => {
                periodButtonRefs.current['AM'] = el;
              }}
              type="button"
              onClick={() => handlePeriodChange('AM')}
              className="text-button-sm relative z-10 flex h-full w-10 cursor-pointer items-center justify-center text-center font-medium text-neutral-400 transition-colors"
            >
              AM
            </button>

            {/* PM Button */}
            <button
              ref={(el) => {
                periodButtonRefs.current['PM'] = el;
              }}
              type="button"
              onClick={() => handlePeriodChange('PM')}
              className="text-button-sm relative z-10 flex h-full w-10 cursor-pointer items-center justify-center text-center font-medium text-neutral-400 transition-colors"
            >
              PM
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
